import { getValidAccessToken } from "../security/auth.service";
import { getCookie } from "../../utils/cookieHandlers";

export interface RequestOptions extends Omit<RequestInit, "body" | "headers"> {
    headers?: Record<string, string>;
    params?: Record<string, string | number | boolean | undefined | null>;
    auth?: boolean;
}

export class HttpClient {
    private readonly baseUrl: string;

    constructor(baseUrl = import.meta.env.VITE_HUB_API_URL ?? "") {
        this.baseUrl = baseUrl;
    }

    private async buildHeaders(auth = true, customHeaders?: Record<string, string>): Promise<Record<string, string>> {
        const headers: Record<string, string> = {
            "Content-Type": "application/json",
        };

        if (auth) {
            const token = await getValidAccessToken();
            if (token) {
                headers.authorization = `Bearer ${token}`;
            }
        }

        const language = typeof document !== "undefined" ? getCookie("language") : null;
        if (language) {
            headers["content-language"] = language;
        }

        if (customHeaders) {
            Object.assign(headers, customHeaders);
        }

        return headers;
    }

    private buildUrl(path: string, params?: Record<string, string | number | boolean | undefined | null>): string {
        let fullUrl: URL;

        if (path.startsWith("http://") || path.startsWith("https://")) {
            fullUrl = new URL(path);
        } else {
            const base = this.baseUrl.endsWith("/") ? this.baseUrl.slice(0, -1) : this.baseUrl;
            const normalizedPath = path.startsWith("/") ? path : `/${path}`;
            const targetUrl = `${base}${normalizedPath}`;
            if (targetUrl.startsWith("http://") || targetUrl.startsWith("https://")) {
                fullUrl = new URL(targetUrl);
            } else {
                const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost";
                fullUrl = new URL(targetUrl, origin);
            }
        }

        if (params) {
            Object.entries(params).forEach(([key, val]) => {
                if (val !== undefined && val !== null) {
                    fullUrl.searchParams.set(key, String(val));
                }
            });
        }

        return fullUrl.toString();
    }

    private async handleResponse<T>(response: Response): Promise<T> {
        if (!response.ok) {
            let errorMessage = `Request failed: ${response.statusText}`;
            try {
                const errorData = await response.json();
                if (errorData?.message) {
                    errorMessage = errorData.message;
                } else if (errorData?.errorMessage) {
                    errorMessage = errorData.errorMessage;
                } else if (errorData?.error?.message) {
                    errorMessage = errorData.error.message;
                }
            } catch {
                // Non-JSON response error
            }
            throw new Error(errorMessage);
        }

        return response.json();
    }

    async get<T>(path: string, options?: RequestOptions): Promise<T> {
        const { auth = true, params, headers: customHeaders, ...fetchInit } = options ?? {};
        const url = this.buildUrl(path, params);
        const headers = await this.buildHeaders(auth, customHeaders);
        const response = await fetch(url, {
            ...fetchInit,
            method: "GET",
            headers,
        });
        return this.handleResponse<T>(response);
    }

    async post<T, B = unknown>(path: string, body?: B, options?: RequestOptions): Promise<T> {
        const { auth = true, params, headers: customHeaders, ...fetchInit } = options ?? {};
        const url = this.buildUrl(path, params);
        const headers = await this.buildHeaders(auth, customHeaders);
        const response = await fetch(url, {
            ...fetchInit,
            method: "POST",
            headers,
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        return this.handleResponse<T>(response);
    }

    async patch<T, B = unknown>(path: string, body?: B, options?: RequestOptions): Promise<T> {
        const { auth = true, params, headers: customHeaders, ...fetchInit } = options ?? {};
        const url = this.buildUrl(path, params);
        const headers = await this.buildHeaders(auth, customHeaders);
        const response = await fetch(url, {
            ...fetchInit,
            method: "PATCH",
            headers,
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        return this.handleResponse<T>(response);
    }

    async delete<T>(path: string, options?: RequestOptions): Promise<T> {
        const { auth = true, params, headers: customHeaders, ...fetchInit } = options ?? {};
        const url = this.buildUrl(path, params);
        const headers = await this.buildHeaders(auth, customHeaders);
        const response = await fetch(url, {
            ...fetchInit,
            method: "DELETE",
            headers,
        });
        return this.handleResponse<T>(response);
    }
}

export const httpClient = new HttpClient();
